import styled, { css } from 'styled-components'
import { EventType } from '../../types'

const eventTypeColors = {
  transaction: '#00ade6',
  sale: '#00b341',
  rental: '#7b61ff',
  permit: '#ff9500',
}

export const ItemContainer = styled.div`
  position: relative;
  margin-bottom: 24px;
`

export const DateLabel = styled.div`
  position: absolute;
  left: 0;
  top: 16px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-tertiary);
  width: 80px;
  white-space: nowrap;
  text-align: center;

  @media (max-width: 768px) {
    position: relative;
    left: 20px;
    top: 0;
    margin-bottom: 8px;
    text-align: left;
    width: auto;
  }
`

export const Marker = styled.div.withConfig({
  shouldForwardProp: (prop) => !['eventType'].includes(prop),
})<{ eventType: EventType }>`
  position: absolute;
  left: 0;
  top: 0px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-tertiary);
  width: 80px;
  white-space: nowrap;
  text-align: center;

  @media (max-width: 768px) {
    text-align: left;
  }
`

export const Content = styled.div`
  margin-left: 100px;
  padding-top: 0;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`

export const EventCard = styled.div.withConfig({
  shouldForwardProp: (prop) => !['eventType', 'hasImage'].includes(prop),
})<{
  eventType: EventType
  hasImage?: boolean
}>`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  transition: all 0.2s;
  cursor: pointer;
  border-left: 3px solid
    ${({ eventType }) => eventTypeColors[eventType] || '#00ade6'};

  &:hover {
    border-color: #999999;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }

  ${({ hasImage }) =>
    hasImage &&
    css`
      padding: 16px;
    `}

  @media (max-width: 768px) {
    padding: 12px;

    ${({ hasImage }) =>
      hasImage &&
      css`
        padding: 12px;
      `}
  }
`

export const EventHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  gap: 12px;
`

export const EventTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`

export const EventStatus = styled.span<{ status?: string }>`
  font-size: 11px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${({ status }) => {
    switch (status) {
      case 'expired':
        return css`
          background: var(--bg-tertiary);
          color: var(--text-tertiary);
        `
      case 'active':
        return css`
          background: rgba(0, 179, 65, 0.1);
          color: var(--success);
        `
      default:
        return css`
          background: var(--bg-secondary);
          color: var(--text-secondary);
        `
    }
  }}
`

export const EventBody = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    gap: 12px;
  }
`

export const EventContent = styled.div`
  flex: 1;
  min-width: 0;
`

export const EventDetails = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 8px;
`

export const EventPrice = styled.div<{ primary?: boolean }>`
  font-size: 18px;
  font-weight: 600;
  color: ${({ primary }) =>
    primary ? 'var(--primary)' : 'var(--text-primary)'};

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

export const EventPriceSub = styled.div`
  font-size: 13px;
  color: var(--text-secondary);
`

export const EventDescription = styled.p`
  font-size: 13px;
  color: var(--text-primary);
  margin: 0 0 12px 0;
  line-height: 1.5;
`

export const EventMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 8px;
`

export const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--text-secondary);

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    font-size: 11px;
  }
`

export const EventImage = styled.div`
  flex-shrink: 0;
  width: 120px;
  height: 80px;
  border-radius: 6px;
  overflow: hidden;
  background: var(--bg-tertiary);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 160px;
  }
`

export const EventFooter = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--border-light);
`

export const OnlineDuration = styled.span`
  font-size: 11px;
  color: var(--text-tertiary);
  font-style: italic;
`
